// src/contexts/StatisticsContext.tsx

import React, {
    createContext,
    useContext,
    useState,
    useEffect,
    ReactNode,
} from 'react';

interface Statistics {
    gamesPlayed: number;
    wins: number;
    losses: number;
    winRate: number;
    currentStreak: number;
    maxStreak: number;
    gamesWon: number;
    averageCluesUsed: number;
    winPercentage: number;
    guessesDistribution: number[]; // Array representing the number of wins at each clue count
}

interface StatisticsContextProps {
    statistics: Statistics;
    updateStatistics: (
        isWin: boolean,
        cluesUsed: number,
        totalClues: number,
        isOverride?: boolean
    ) => void;
}

const defaultStatistics: Statistics = {
    gamesPlayed: 0,
    wins: 0,
    losses: 0,
    winRate: 0,
    currentStreak: 0,
    maxStreak: 0,
    gamesWon:0,
    winPercentage: 0,
    averageCluesUsed: 0,
    guessesDistribution: [],
};

const StatisticsContext = createContext<StatisticsContextProps>({
    statistics: defaultStatistics,
    updateStatistics: () => {},
});

export const useStatistics = () => useContext(StatisticsContext);

interface StatisticsProviderProps {
    children: ReactNode;
}

export const StatisticsProvider: React.FC<StatisticsProviderProps> = ({
                                                                          children,
                                                                      }) => {
    const [statistics, setStatistics] = useState<Statistics>(defaultStatistics);

    useEffect(() => {
        const storedStats = localStorage.getItem('statistics');
        if (storedStats) {
            setStatistics(JSON.parse(storedStats));
        }
    }, []);

    const updateStatistics = (
        isWin: boolean,
        cluesUsed: number,
        totalClues: number,
        isOverride: boolean = false
    ) => {
        setStatistics((prevStats) => {
            let updatedStats = { ...prevStats };

            if (isOverride) {
                // Adjust previous game result without changing gamesPlayed
                // Assume that the previous game was a loss and changing it to a win

                // Increment wins and decrement losses
                updatedStats.wins += 1;
                updatedStats.losses = Math.max(updatedStats.losses - 1, 0);

                // Update current streak and max streak
                updatedStats.currentStreak += 1;
                if (updatedStats.currentStreak > updatedStats.maxStreak) {
                    updatedStats.maxStreak = updatedStats.currentStreak;
                }

                // Update guesses distribution
                while (updatedStats.guessesDistribution.length < totalClues) {
                    updatedStats.guessesDistribution.push(0);
                }
                updatedStats.guessesDistribution[cluesUsed - 1] += 1;

                // Recalculate win rate
                updatedStats.winRate =
                    updatedStats.gamesPlayed > 0
                        ? (updatedStats.wins / updatedStats.gamesPlayed) * 100
                        : 0;
            } else {
                // Regular update
                updatedStats.gamesPlayed += 1;

                if (isWin) {
                    updatedStats.wins += 1;
                    updatedStats.currentStreak += 1;
                    if (updatedStats.currentStreak > updatedStats.maxStreak) {
                        updatedStats.maxStreak = updatedStats.currentStreak;
                    }
                    // Update guesses distribution
                    while (updatedStats.guessesDistribution.length < totalClues) {
                        updatedStats.guessesDistribution.push(0);
                    }
                    updatedStats.guessesDistribution[cluesUsed - 1] += 1;
                } else {
                    updatedStats.losses += 1;
                    updatedStats.currentStreak = 0;
                }

                // Recalculate win rate
                updatedStats.winRate =
                    updatedStats.gamesPlayed > 0
                        ? (updatedStats.wins / updatedStats.gamesPlayed) * 100
                        : 0;
            }

            // Save to local storage
            localStorage.setItem('statistics', JSON.stringify(updatedStats));

            return updatedStats;
        });
    };

    return (
        <StatisticsContext.Provider value={{ statistics, updateStatistics }}>
            {children}
        </StatisticsContext.Provider>
    );
};
